import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useVelocity,
    useSpring,
    useAnimationFrame,
    useMotionValue,
    useReducedMotion,
} from "framer-motion";

const VINYL =
    "https://static.prod-images.emergentagent.com/jobs/8e02870b-796d-47e6-9225-636f1c3ce92e/images/1ac5c11bcf7b8518b5ae434953de1a304e442a0a168f6a5c6b90c77251d4104a.png";

export default function Vision() {
    const ref = useRef(null);
    const reduce = useReducedMotion();

    // Page-wide scroll velocity → smoothed
    const { scrollY } = useScroll();
    const rawVelocity = useVelocity(scrollY);
    const smoothVelocity = useSpring(rawVelocity, {
        damping: 60,
        stiffness: 350,
        mass: 0.4,
    });

    // Section-relative progress → gentle vertical parallax
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
    const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.14, 1.08]);

    // Rotation: idle baseline + velocity-driven acceleration
    const rotate = useMotionValue(0);
    useAnimationFrame((_t, delta) => {
        if (reduce) return;
        const velocity = smoothVelocity.get(); // px/s (signed)
        const spinFromScroll = velocity * (delta / 1000) * 0.06; // 0.06°/px at 1s
        const idle = delta * 0.006; // ~0.006°/ms = ~5° per second at rest
        rotate.set(rotate.get() + spinFromScroll + idle);
    });

    return (
        <section
            ref={ref}
            className="relative px-6 md:px-10 py-16 md:py-24 overflow-hidden bg-[#0A0A0A] text-white border-y-4 border-black"
            data-testid="vision-section"
        >
            {/* Parallax + velocity-rotating vinyl */}
            <motion.img
                src={VINYL}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover opacity-25 will-change-transform"
                style={{
                    y,
                    scale,
                    rotate,
                    transformOrigin: "center center",
                }}
            />

            {/* Layered atmosphere */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-black/50 to-[#0A0A0A]" aria-hidden />
            <motion.div
                className="pointer-events-none absolute top-1/4 -left-20 w-[400px] h-[400px] rounded-full bg-[#C2410C] opacity-25 blur-3xl"
                style={{ y: useTransform(scrollYProgress, [0, 1], ["-30%", "30%"]) }}
                aria-hidden
            />
            <motion.div
                className="pointer-events-none absolute bottom-1/4 -right-20 w-[400px] h-[400px] rounded-full bg-[#D4A017] opacity-15 blur-3xl"
                style={{ y: useTransform(scrollYProgress, [0, 1], ["30%", "-30%"]) }}
                aria-hidden
            />

            <div className="relative max-w-7xl mx-auto text-center reveal">
                <h2 className="font-display tracking-tighter font-black leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
                    Imagine hearing
                    <br />
                    your <em className="not-italic bg-[#C2410C] text-white px-4 -rotate-1 inline-block">own</em> song
                    <br />
                    on Spotify.
                </h2>

                <ul className="mt-12 grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto text-left">
                    {[
                        "Sending your music to friends",
                        "Hearing your voice on streaming",
                        "Seeing your artist profile go live",
                        "Creating something you'll have forever",
                    ].map((t, i) => (
                        <li
                            key={t}
                            className="flex items-baseline gap-3 text-white text-base md:text-lg"
                        >
                            <span className="font-mono text-xs text-[#D4A017] font-bold">
                                0{i + 1}
                            </span>
                            <span>{t}</span>
                        </li>
                    ))}
                </ul>

                <p className="mt-12 font-display text-2xl md:text-3xl text-[#D4A017]">
                    That moment is what we build.
                </p>
            </div>
        </section>
    );
}
